import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Paper,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Icon } from '@iconify/react';
import { ENV_CONFIG } from '../config/environment';

// Add DM Sans font import
const TUTORIAL_CSS = `
  @import url('${ENV_CONFIG.EXTERNAL.GOOGLE_FONTS_URL}');
`;

const TutorialDialog = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expandedPanel, setExpandedPanel] = useState('getting-started');

  // Add CSS to head if not already present
  React.useEffect(() => {
    if (!document.getElementById('tutorial-styles')) {
      const style = document.createElement('style');
      style.id = 'tutorial-styles';
      style.textContent = TUTORIAL_CSS;
      document.head.appendChild(style);
    }
  }, []);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : false);
  };

  const tutorialSections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: 'material-symbols:rocket-launch',
      color: '#4caf50',
      content: {
        description: 'Learn the basics of Parts360 and how to get started quickly.',
        features: [
          {
            title: 'Sign In',
            description: 'Use your Microsoft account to sign in and sync your sessions across devices.',
            icon: 'mdi:login'
          },
          {
            title: 'Create New Session',
            description: 'Click the "New Session" button to start a fresh conversation with the AI.',
            icon: 'material-symbols:add-circle'
          },
          {
            title: 'Navigate Sessions',
            description: 'Browse your conversation history organized by date in the sidebar.',
            icon: 'material-symbols:history'
          },
          {
            title: 'Ask Questions',
            description: 'Type your questions in the chat input and get intelligent responses.',
            icon: 'material-symbols:chat'
          }
        ]
      }
    },
    {
      id: 'ai-features',
      title: 'AI Features',
      icon: 'hugeicons:ai-brain-02',
      color: '#2196f3',
      content: {
        description: 'Discover the powerful AI capabilities and different agent types.',
        features: [
          {
            title: 'SQL Agent',
            description: 'Direct database queries for precise data retrieval and analysis.',
            icon: 'mdi:database'
          },
          {
            title: 'AI Search',
            description: 'Intelligent search across all your data with natural language processing.',
            icon: 'material-symbols:search'
          },
          {
            title: 'Data Tables',
            description: 'Interactive tables with sorting, filtering, and export capabilities.',
            icon: 'material-symbols:table'
          },
          {
            title: 'Feedback System',
            description: 'Rate AI responses to help improve the system over time.',
            icon: 'material-symbols:feedback'
          }
        ]
      }
    },
    {
      id: 'parts-queries',
      title: 'Parts & Inventory',
      icon: 'material-symbols:inventory-2',
      color: '#ff9800',
      content: {
        description: 'Learn how to query parts information, inventory, and availability.',
        features: [
          {
            title: 'Part Availability',
            description: 'Check if specific parts are in stock and their current location.',
            icon: 'material-symbols:check-circle'
          },
          {
            title: 'Inventory Levels',
            description: 'Get real-time inventory counts and warehouse locations.',
            icon: 'material-symbols:warehouse'
          },
          {
            title: 'Cost Information',
            description: 'Access pricing data and cost analysis for parts and components.',
            icon: 'material-symbols:attach-money'
          },
          {
            title: 'Historical Data',
            description: 'Review past quotes, proposals, and pricing trends.',
            icon: 'material-symbols:trending-up'
          }
        ]
      }
    },
    {
      id: 'data-analysis',
      title: 'Data Analysis',
      icon: 'material-symbols:analytics',
      color: '#9c27b0',
      content: {
        description: 'Advanced data analysis features and reporting capabilities.',
        features: [
          {
            title: 'Custom Queries',
            description: 'Write natural language queries to get specific data insights.',
            icon: 'material-symbols:query-stats'
          },
          {
            title: 'Export Data',
            description: 'Download query results as Excel files for further analysis.',
            icon: 'material-symbols:download'
          },
          {
            title: 'Data Visualization',
            description: 'View data in interactive tables with sorting and filtering.',
            icon: 'material-symbols:bar-chart'
          },
          {
            title: 'Cross-Reference',
            description: 'Compare data across different tables and time periods.',
            icon: 'material-symbols:compare'
          }
        ]
      }
    },
    {
      id: 'admin-features',
      title: 'Admin Features',
      icon: 'material-symbols:admin-panel-settings',
      color: '#f44336',
      content: {
        description: 'Administrative tools and user management capabilities.',
        features: [
          {
            title: 'User Management',
            description: 'Add, edit, and manage user accounts and permissions.',
            icon: 'material-symbols:person-add'
          },
          {
            title: 'Access Control',
            description: 'Set data access levels and role-based permissions.',
            icon: 'material-symbols:security'
          },
          {
            title: 'Table Permissions',
            description: 'Configure which data tables users can access.',
            icon: 'material-symbols:table-rows'
          },
          {
            title: 'Download Limits',
            description: 'Set and monitor data download restrictions.',
            icon: 'material-symbols:download-done'
          }
        ]
      }
    },
    {
      id: 'tips-tricks',
      title: 'Tips & Tricks',
      icon: 'material-symbols:lightbulb',
      color: '#ffeb3b',
      content: {
        description: 'Pro tips to get the most out of Parts360.',
        features: [
          {
            title: 'Use Specific Part Numbers',
            description: 'Include exact part numbers in your queries for more accurate results.',
            icon: 'material-symbols:precision-manufacturing'
          },
          {
            title: 'Keyboard Shortcuts',
            description: 'Press Enter to send messages quickly, use Ctrl+K for quick navigation.',
            icon: 'material-symbols:keyboard'
          },          {
            title: 'Session Organization',
            description: 'Use descriptive conversation topics to easily find old sessions.',
            icon: 'material-symbols:folder-open'
          },
          {
            title: 'Feedback Helps',
            description: 'Rate responses to help the AI learn and improve over time.',
            icon: 'material-symbols:thumb-up'
          }
        ]
      }
    }
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '20px',
          maxHeight: '90vh',
          fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
          boxShadow: '0 32px 64px -12px rgba(0, 0, 0, 0.15), 0 8px 16px -4px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(32, 101, 209, 0.08)',
          overflow: 'hidden'
        }
      }}
    >      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #2065D1 0%, #1976D2 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 4,
          px: 4,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -1,
            left: 0,
            right: 0,
            height: 1,
            background: 'rgba(255, 255, 255, 0.2)',
          }
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '16px',
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Icon icon="material-symbols:school" width={32} height={32} />
        </Box>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, fontSize: '28px' }}>
            Parts360 Tutorial
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, fontSize: '16px' }}>
            Complete guide to all features and capabilities
          </Typography>
        </Box>
      </DialogTitle>      <DialogContent sx={{ p: 0, backgroundColor: '#fafbfc' }}>
        <Box sx={{ p: 4, pb: 2 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, fontSize: '16px', lineHeight: 1.6 }}>
            Welcome to the comprehensive Parts360 tutorial! Explore each section below to learn about different features and capabilities.
          </Typography>
        </Box>        {tutorialSections.map((section, index) => (
          <Box
            key={section.id}
            sx={{
              backgroundColor: 'white',
              borderRadius: '16px',
              mb: 2,
              mx: 3,
              overflow: 'hidden',
              border: '1px solid rgba(0, 0, 0, 0.08)',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 8px 24px -6px rgba(0, 0, 0, 0.12)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            {/* Section Header */}
            <Box
              onClick={() => setExpandedPanel(expandedPanel === section.id ? null : section.id)}
              sx={{
                py: 3,
                px: 4,
                cursor: 'pointer',
                borderRadius: '16px',
                '&:hover': {
                  bgcolor: `${section.color}08`
                },
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                width: '100%'
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: `${section.color}15`,
                  border: `2px solid ${section.color}30`,
                  transition: 'all 0.2s ease'
                }}
              >
                <Icon icon={section.icon} width={28} height={28} style={{ color: section.color }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '18px' }}>
                  {section.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '14px', mt: 0.5 }}>
                  {section.content.description}
                </Typography>
              </Box>
              <Chip
                label={`${section.content.features.length} features`}
                size="small"
                sx={{
                  bgcolor: `${section.color}15`,
                  color: section.color,
                  fontWeight: 600,
                  fontSize: '12px',
                  height: 28
                }}
              />
              <Icon 
                icon="material-symbols:expand-more" 
                width={24} 
                height={24} 
                style={{ 
                  color: section.color,
                  transform: expandedPanel === section.id ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease'
                }} 
              />
            </Box>

            {/* Section Content */}
            {expandedPanel === section.id && (
              <Box sx={{ pt: 0, pb: 4, px: 4 }}>
                <List sx={{ py: 0 }}>
                  {section.content.features.map((feature, index) => (
                    <ListItem key={index} sx={{ px: 0, py: 2 }}>
                      <ListItemIcon sx={{ minWidth: 56 }}>
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: `${section.color}10`,
                            border: `1px solid ${section.color}20`
                          }}
                        >
                          <Icon icon={feature.icon} width={20} height={20} style={{ color: section.color }} />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5, fontSize: '16px' }}>
                            {feature.title}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, fontSize: '14px' }}>
                            {feature.description}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        ))}<Paper sx={{ 
          m: 4, 
          p: 4, 
          background: 'linear-gradient(135deg, #2065D1 0%, #1976D2 100%)', 
          color: 'white',
          borderRadius: '20px',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 1,
            background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)'
          }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(10px)',
              }}
            >
              <Icon icon="material-symbols:support-agent" width={24} height={24} />
            </Box>
          </Box>
          <Typography variant="body1" sx={{ mb: 3, opacity: 0.9, fontSize: '16px', lineHeight: 1.6 }}>
            If you need additional assistance or have questions not covered in this tutorial, please contact your system administrator or use the feedback system within the application.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip
              label="Take Quick Tour"
              size="medium"
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 500,
                fontSize: '14px',
                height: 36,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
              }}
            />
            <Chip
              label="Contact Support"
              size="medium"
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 500,
                fontSize: '14px',
                height: 36,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
              }}
            />
          </Box>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={onClose}
          variant="contained"
          size="large"
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            px: 4
          }}
        >
          Got it!
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TutorialDialog;
